import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/feature/AdminLayout";
import { adminStats, revenueData, topSellingProducts, categoryBreakdown } from "@/mocks/admin";
import { mockOrders } from "@/mocks/orders";

function StatCard({ label, value, growth, icon, prefix }: {
  label: string;
  value: string;
  growth: number;
  icon: string;
  prefix?: string;
}) {
  const isPositive = growth >= 0;
  return (
    <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
          <i className={`${icon} text-lg text-primary-600`}></i>
        </div>
        <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
          isPositive ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-800"
        }`}>
          <i className={`text-[10px] ${isPositive ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
          {Math.abs(growth)}%
        </span>
      </div>
      <p className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-1">{prefix}{value}</p>
      <p className="text-xs text-foreground-500">{label}</p>
    </div>
  );
}

function buildSmoothPath(pts: { x: number; y: number }[], tension: number): string {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function RevenueChart() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const chartHeight = 180;
  const padding = { top: 10, right: 8, bottom: 26, left: 55 };
  const viewBoxW = 960;
  const viewBoxH = chartHeight + padding.top + padding.bottom;
  const chartW = viewBoxW - padding.left - padding.right;

  const maxRevenue = Math.max(...revenueData.map((d) => d.revenue));
  const maxOrders = Math.max(...revenueData.map((d) => d.orders));
  const chartBottom = padding.top + chartHeight;

  const points = revenueData.map((d, i) => ({
    x: padding.left + (i / (revenueData.length - 1)) * chartW,
    yRevenue: padding.top + chartHeight - (d.revenue / maxRevenue) * chartHeight,
    yOrders: padding.top + chartHeight - (d.orders / maxOrders) * chartHeight,
    ...d,
  }));

  const revenuePts = points.map((p) => ({ x: p.x, y: p.yRevenue }));
  const ordersPts = points.map((p) => ({ x: p.x, y: p.yOrders }));

  const revenueLinePath = buildSmoothPath(revenuePts, 0.25);
  const ordersLinePath = buildSmoothPath(ordersPts, 0.25);

  const areaPath = `${revenueLinePath} L ${points[points.length - 1].x} ${chartBottom} L ${points[0].x} ${chartBottom} Z`;

  const gridLevels = [0, 0.25, 0.5, 0.75, 1];

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * viewBoxW;
      const ratio = (mouseX - padding.left) / chartW;
      const idx = Math.round(ratio * (revenueData.length - 1));
      const clamped = Math.max(0, Math.min(revenueData.length - 1, idx));
      setHoveredIndex(clamped);
    },
    [],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  return (
    <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground-900 font-heading">Revenue Overview</h3>
          <p className="text-xs text-foreground-500 mt-0.5">Weekly revenue for the last 6 months</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full bg-primary-500 inline-block"></span>
            <span className="text-xs text-foreground-500">Revenue</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full bg-secondary-400 inline-block" style={{ background: 'repeating-linear-gradient(90deg, transparent, transparent 3px, oklch(var(--secondary-400)) 3px, oklch(var(--secondary-400)) 6px)' }}></span>
            <span className="text-xs text-foreground-500">Orders</span>
          </div>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${viewBoxW} ${viewBoxH}`}
        className="w-full"
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ overflow: "visible" }}
      >
        <defs>
          <linearGradient id="revenueAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity="0.28" />
            <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="revenueLineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="oklch(var(--primary-400))" />
            <stop offset="100%" stopColor="oklch(var(--primary-500))" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {gridLevels.map((pct) => {
          const y = padding.top + chartHeight * (1 - pct);
          const val = Math.round(maxRevenue * (1 - pct));
          return (
            <g key={pct}>
              <line
                x1={padding.left}
                y1={y}
                x2={viewBoxW - padding.right}
                y2={y}
                stroke="oklch(var(--foreground-200) / 0.5)"
                strokeWidth="1"
                strokeDasharray={pct === 0 ? "none" : "3 4"}
              />
              <text
                x={padding.left - 8}
                y={y + 1}
                textAnchor="end"
                className="text-[11px]"
                fill="oklch(var(--foreground-400))"
              >
                ${val.toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path
          d={areaPath}
          fill="url(#revenueAreaGrad)"
          stroke="none"
        />

        {/* Orders line (dashed, behind revenue) */}
        <path
          d={ordersLinePath}
          fill="none"
          stroke="oklch(var(--secondary-400))"
          strokeWidth="1.5"
          strokeDasharray="6 4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Revenue line */}
        <path
          d={revenueLinePath}
          fill="none"
          stroke="url(#revenueLineGrad)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data point dots */}
        {points.map((p, i) => {
          const isActive = hoveredIndex === i;
          return (
            <g key={i}>
              {isActive && (
                <>
                  {/* Vertical guide line */}
                  <line
                    x1={p.x}
                    y1={padding.top}
                    x2={p.x}
                    y2={chartBottom}
                    stroke="oklch(var(--foreground-300) / 0.5)"
                    strokeWidth="1"
                    strokeDasharray="2 3"
                  />
                  {/* Outer ring */}
                  <circle
                    cx={p.x}
                    cy={p.yRevenue}
                    r="10"
                    fill="oklch(var(--primary-500) / 0.15)"
                    stroke="oklch(var(--primary-500) / 0.35)"
                    strokeWidth="1.5"
                  />
                </>
              )}
              {/* Dot */}
              <circle
                cx={p.x}
                cy={p.yRevenue}
                r={isActive ? 4.5 : 2.5}
                fill={isActive ? "oklch(var(--primary-500))" : "oklch(var(--primary-400))"}
                stroke={isActive ? "oklch(var(--background-50))" : "none"}
                strokeWidth="2"
              />
            </g>
          );
        })}

        {/* Hover capture zone (transparent) */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartW}
          height={chartHeight}
          fill="transparent"
          style={{ cursor: "pointer" }}
        />
      </svg>

      {/* Tooltip */}
      {hoveredIndex !== null && points[hoveredIndex] && (
        <div className="flex justify-center mt-1">
          <div className="inline-flex items-center gap-4 bg-foreground-900 text-background-50 text-xs px-3 py-1.5 rounded-lg">
            <span className="font-semibold whitespace-nowrap">{points[hoveredIndex].date}</span>
            <span className="whitespace-nowrap opacity-80">${points[hoveredIndex].revenue.toLocaleString()}</span>
            <span className="whitespace-nowrap opacity-60">{points[hoveredIndex].orders} orders</span>
          </div>
        </div>
      )}

      {/* X-axis labels */}
      <div className="flex mt-1.5" style={{ paddingLeft: "55px", paddingRight: "8px" }}>
        {revenueData.filter((_, i) => i % 5 === 0).map((d, i) => (
          <div key={i} className="flex-1 text-[10px] text-foreground-400 text-center min-w-0 leading-tight">
            {d.date}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { t } = useTranslation();

  const recentOrders = useMemo(() => {
    return [...mockOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  }, []);

  const statusColor = (status: string) => {
    switch (status) {
      case "delivered": return "bg-accent-100 text-accent-800";
      case "shipped": return "bg-secondary-100 text-secondary-800";
      case "processing": return "bg-primary-100 text-primary-800";
      case "cancelled": return "bg-foreground-100 text-foreground-600";
      default: return "bg-background-200 text-foreground-500";
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950">{t("admin.dashboard")}</h1>
          <p className="text-sm text-foreground-500 mt-1">Welcome back! Here's what's happening with your store today.</p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Revenue"
            value={adminStats.totalRevenue.toLocaleString()}
            growth={adminStats.revenueGrowth}
            icon="ri-money-dollar-circle-line"
            prefix="$"
          />
          <StatCard
            label="Total Orders"
            value={adminStats.totalOrders.toLocaleString()}
            growth={adminStats.ordersGrowth}
            icon="ri-shopping-bag-3-line"
          />
          <StatCard
            label="Products"
            value={adminStats.totalProducts.toLocaleString()}
            growth={adminStats.productsGrowth}
            icon="ri-store-2-line"
          />
          <StatCard
            label="Customers"
            value={adminStats.totalCustomers.toLocaleString()}
            growth={adminStats.customersGrowth}
            icon="ri-group-line"
          />
        </div>

        {/* Revenue chart */}
        <RevenueChart />

        {/* Top products + Category breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top-Selling Products */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground-900 font-heading">Top-Selling Products</h3>
              <a
                href="/admin/products"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                View All
              </a>
            </div>
            <div className="divide-y divide-background-100">
              {topSellingProducts.map((product, index) => (
                <a
                  key={product.id}
                  href={`/admin/products/${product.id}/edit`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-background-100/60 transition-colors cursor-pointer"
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                    index === 0 ? "bg-accent-500 text-background-50" :
                    index === 1 ? "bg-primary-100 text-primary-700" :
                    index === 2 ? "bg-secondary-100 text-secondary-700" :
                    "bg-foreground-100 text-foreground-500"
                  }`}>{index + 1}</span>
                  <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                    <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{product.name}</p>
                    <span className="text-[11px] text-foreground-400">{product.category}</span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-foreground-900">${product.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <span className="text-[11px] text-foreground-400">{product.unitsSold} sold</span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-900 font-heading">Revenue by Category</h3>
            </div>
            <div className="p-5 space-y-4">
              {categoryBreakdown.map((cat) => (
                <div key={cat.name}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-900">${cat.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="text-[11px] font-medium text-foreground-400 w-10 text-right">{cat.percentage}%</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${cat.colorClass}`}
                      style={{ width: `${cat.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent orders */}
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground-900 font-heading">Recent Orders</h3>
            <a
              href="/admin/orders"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              View All
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70">
                  {["Order", "Customer", "Date", "Status", "Total"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <tr key={order.id} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <a href={`/admin/orders/${order.id}`} className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                        {order.orderNumber}
                      </a>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-700">{order.shippingAddress.fullName}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-foreground-500 whitespace-nowrap">{order.date}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap ${statusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-semibold text-foreground-900">${order.total.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}